import {
  bar as barText,
  blank,
  box,
  chart,
  divider,
  grid,
  hstack,
  lines,
  separator,
  statusbar,
  table,
  vstack,
  padLine,
  type Block,
  type Column,
  type LayoutFn,
  type Line,
  type Segment,
  type Theme,
} from "../termui";
import { fromJson } from "../document/json";
import { fromMarkdown } from "../document/markdown";
import {
  headingColor,
  isNumericValue,
  renderPrefixedText,
  wrapText,
} from "../document/common";
import type { MarkdownThemeOverrides } from "../document/types";
import type { TermAction, TermIRNode } from "./ir";

export type LayoutFromIROptions = {
  theme: Theme;
  overrides?: MarkdownThemeOverrides;
  onAction?: (action: TermAction) => void;
};

function stackLayouts(children: LayoutFn[], gap: number): LayoutFn {
  if (children.length === 0) return blank();
  if (children.length === 1 && gap <= 0) return children[0]!;
  if (gap <= 0) return vstack(...children);

  const expanded: LayoutFn[] = [];
  for (let i = 0; i < children.length; i++) {
    if (i > 0) {
      for (let j = 0; j < gap; j++) expanded.push(blank());
    }
    expanded.push(children[i]!);
  }
  return vstack(...expanded);
}

function paragraphLayout(text: string, color?: string): LayoutFn {
  return (w) => {
    const wrapped = wrapText(text, w);
    if (!color) return wrapped.map((line) => [line]);
    return wrapped.map((line) => [{ text: line, color }]);
  };
}

function headingLayout(
  node: Extract<TermIRNode, { kind: "heading" }>,
  options: LayoutFromIROptions,
): LayoutFn {
  const md = options.theme.markdown;
  return (w) =>
    renderPrefixedText(
      node.text,
      w,
      {
        text: "#".repeat(node.level) + " ",
        color: node.markerColor ?? options.overrides?.headingMarker ?? md.headingMarker,
      },
      " ".repeat(node.level + 1),
      {
        color: headingColor(node.level, options.theme, options.overrides),
        bold: node.level <= (options.overrides?.headingBoldMaxLevel ?? md.headingBoldMaxLevel),
      },
    );
}

function listLayout(
  node: Extract<TermIRNode, { kind: "list" }>,
  options: LayoutFromIROptions,
): LayoutFn {
  return (w) => {
    const out: Line[] = [];
    const md = options.theme.markdown;

    if (node.ordered) {
      const digits = String(node.start + node.items.length - 1).length;
      for (let index = 0; index < node.items.length; index++) {
        const item = node.items[index]!;
        const n = String(node.start + index).padStart(digits, " ");
        out.push(
          ...renderPrefixedText(
            item,
            w,
            { text: `${n}. `, color: options.overrides?.orderedMarker ?? md.orderedMarker },
            " ".repeat(digits + 2),
          ),
        );
      }
      return out.length > 0 ? out : blank()(w);
    }

    for (const item of node.items) {
      out.push(
        ...renderPrefixedText(
          item,
          w,
          { text: " • ", color: options.overrides?.unorderedBullet ?? md.unorderedBullet },
          "   ",
        ),
      );
    }
    return out.length > 0 ? out : blank()(w);
  };
}

function blockquoteLayout(
  node: Extract<TermIRNode, { kind: "blockquote" }>,
  options: LayoutFromIROptions,
): LayoutFn {
  return (w) =>
    renderPrefixedText(
      node.text,
      w,
      {
        text: "▌ ",
        color: node.markerColor ?? options.overrides?.quoteMarker ?? options.theme.markdown.quoteMarker,
      },
      "  ",
      {
        color: node.color ?? options.overrides?.quoteText ?? options.theme.markdown.quoteText,
        dim: true,
      },
    );
}

function preLayout(
  node: Extract<TermIRNode, { kind: "pre" }>,
  options: LayoutFromIROptions,
): LayoutFn {
  return box(
    lines(
      node.lines.length > 0
        ? node.lines.map((line) => [{ text: line || " ", color: node.textColor }])
        : [[{ text: " ", color: node.emptyColor, dim: true }]],
    ),
    {
      title: node.language ? `code:${node.language}` : "code",
      borderColor: node.borderColor,
    },
  );
}

function tableLayout(node: Extract<TermIRNode, { kind: "table" }>): LayoutFn {
  if (node.headers.length === 0) return paragraphLayout("(empty table)");

  const keys = node.headers.map((_, index) => `c${index}`);
  const columns: Column<Record<string, string>>[] = node.headers.map((header, index) => ({
    key: keys[index]!,
    title: header,
    align: node.rows.every((row) => isNumericValue(row[index] ?? "")) ? "right" : "left",
    headerColor: node.headerColor,
  }));

  const rows = node.rows.map((row) => {
    const data: Record<string, string> = {};
    for (let index = 0; index < keys.length; index++) {
      data[keys[index]!] = row[index] ?? "";
    }
    return data;
  });

  return table(columns, rows, { borderColor: node.borderColor });
}

function setNativeInputValue(input: HTMLInputElement, value: string): void {
  const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value");
  if (!descriptor?.set) throw new Error("Missing HTMLInputElement.value setter");
  descriptor.set.call(input, value);
}

function setNativeTextAreaValue(area: HTMLTextAreaElement, value: string): void {
  const descriptor = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value");
  if (!descriptor?.set) throw new Error("Missing HTMLTextAreaElement.value setter");
  descriptor.set.call(area, value);
}

function dispatchInputAndChange(el: HTMLElement): void {
  el.dispatchEvent(new Event("input", { bubbles: true, cancelable: true, composed: true }));
  el.dispatchEvent(new Event("change", { bubbles: true, cancelable: true, composed: true }));
}

function editInputValue(input: HTMLInputElement): void {
  const next = window.prompt(input.name || input.id || "value", input.value);
  if (next === null || next === input.value) return;
  setNativeInputValue(input, next);
  dispatchInputAndChange(input);
}

function editTextAreaValue(area: HTMLTextAreaElement): void {
  const next = window.prompt(area.name || area.id || "value", area.value);
  if (next === null || next === area.value) return;
  setNativeTextAreaValue(area, next);
  dispatchInputAndChange(area);
}

function cycleSelect(select: HTMLSelectElement): void {
  const enabledOptions = Array.from(select.options).filter((option) => !option.disabled);
  if (enabledOptions.length === 0) return;

  const current = select.selectedOptions[0];
  const currentIndex = current ? enabledOptions.indexOf(current) : -1;
  const nextOption = enabledOptions[(currentIndex + 1 + enabledOptions.length) % enabledOptions.length]!;
  select.value = nextOption.value;
  dispatchInputAndChange(select);
}

function activateSourceElement(sourceElement: Element): void {
  if (sourceElement instanceof HTMLInputElement) {
    if (sourceElement.disabled) return;
    const type = sourceElement.type.toLowerCase();
    if (
      type === "checkbox" ||
      type === "radio" ||
      type === "button" ||
      type === "submit" ||
      type === "reset" ||
      type === "file" ||
      type === "color" ||
      type === "date" ||
      type === "datetime-local" ||
      type === "month" ||
      type === "time" ||
      type === "week"
    ) {
      sourceElement.click();
      return;
    }
    editInputValue(sourceElement);
    return;
  }

  if (sourceElement instanceof HTMLTextAreaElement) {
    if (sourceElement.disabled) return;
    editTextAreaValue(sourceElement);
    return;
  }

  if (sourceElement instanceof HTMLSelectElement) {
    if (sourceElement.disabled) return;
    cycleSelect(sourceElement);
    return;
  }

  if (sourceElement instanceof HTMLDetailsElement) {
    sourceElement.open = !sourceElement.open;
    sourceElement.dispatchEvent(new Event("toggle", { bubbles: true, cancelable: false, composed: true }));
    return;
  }

  if (sourceElement instanceof HTMLElement) {
    sourceElement.click();
  }
}

function actionHandler(action: TermAction, options: LayoutFromIROptions): (() => void) | undefined {
  if (!options.onAction && !action.sourceElement) return undefined;
  return () => {
    if (action.sourceElement) activateSourceElement(action.sourceElement);
    options.onAction?.(action);
  };
}

function buttonLayout(
  node: Extract<TermIRNode, { kind: "button" }>,
  options: LayoutFromIROptions,
): LayoutFn {
  const color = node.color ?? options.theme.components.button.fg;
  const bg = node.bg ?? options.theme.components.button.bg;
  const segment: Segment = {
    text: `[ ${node.label} ]`,
    color,
    bg,
    interactionId: node.action.key,
    onClick: actionHandler(node.action, options),
  };
  return lines([[segment]]);
}

function linkLayout(
  node: Extract<TermIRNode, { kind: "link" }>,
  options: LayoutFromIROptions,
): LayoutFn {
  const segment: Segment = {
    text: node.text,
    color: node.color ?? options.theme.semantic.info,
    interactionId: node.action.key,
    onClick: actionHandler(node.action, options),
  };
  return lines([[segment]]);
}

function barLayout(node: Extract<TermIRNode, { kind: "bar" }>): LayoutFn {
  return lines((w) => {
    const max = Number.isFinite(node.max) && node.max > 0 ? node.max : 100;
    const value = Number.isFinite(node.value) ? node.value : 0;
    const label = node.label ? `${node.label} ` : "";
    const showPercent = node.showPercent !== false;
    const percent = Math.max(0, Math.min(100, Math.round((value / max) * 100)));
    const percentText = showPercent ? ` ${percent}%` : "";

    const available = Math.max(1, w - label.length - percentText.length);
    const barWidth = Math.max(1, Math.min(node.width ?? available, available));

    const line: Line = [];
    if (label) line.push(label);
    line.push({ text: barText(value, max, barWidth), color: node.color });
    if (percentText) line.push(percentText);
    return [line];
  });
}

function childrenToLayouts(children: TermIRNode[], options: LayoutFromIROptions): LayoutFn[] {
  const out: LayoutFn[] = [];
  for (const child of children) {
    out.push(layoutFromIR(child, options));
  }
  return out;
}

export function layoutFromIR(node: TermIRNode, options: LayoutFromIROptions): LayoutFn {
  switch (node.kind) {
    case "stack":
      return stackLayouts(childrenToLayouts(node.children, options), node.gap);
    case "hstack":
      return hstack(childrenToLayouts(node.children, options), {
        gap: node.gap,
        widths: node.widths,
      });
    case "grid":
      return grid(childrenToLayouts(node.children, options), {
        cols: node.cols,
        gap: node.gap,
        rowGap: node.rowGap,
      });
    case "box": {
      const content = stackLayouts(childrenToLayouts(node.children, options), node.gap);
      return box(content, {
        title: node.title,
        borderColor: node.borderColor ?? options.theme.semantic.border,
      });
    }
    case "blank":
      return blank(node.lines);
    case "separator":
      return separator(node.color ?? options.theme.semantic.border, node.char);
    case "divider":
      return divider(node.label, {
        color: node.color ?? options.theme.semantic.border,
        char: node.char,
        labelColor: node.labelColor,
      });
    case "paragraph":
      return paragraphLayout(node.text, node.color);
    case "heading":
      return headingLayout(node, options);
    case "list":
      return listLayout(node, options);
    case "blockquote":
      return blockquoteLayout(node, options);
    case "pre":
      return preLayout(node, options);
    case "markdown":
      return fromMarkdown(node.value, {
        theme: options.theme,
        overrides: options.overrides,
      });
    case "json":
      return fromJson(node.value, {
        theme: options.theme,
        title: node.title,
      });
    case "chart":
      return chart(node.data, {
        height: node.height,
        color: node.color,
        axisColor: node.axisColor,
        fill: node.fill,
      });
    case "statusbar":
      return statusbar(
        [{ text: node.left }],
        [{ text: node.right }],
        {
          bg: node.bg,
          color: node.color,
          theme: options.theme,
        },
      );
    case "bar":
      return barLayout(node);
    case "table":
      return tableLayout(node);
    case "button":
      return buttonLayout(node, options);
    case "link":
      return linkLayout(node, options);
    case "media": {
      const heightLines = node.heightLines ?? 6;
      return (w) => {
        const widthCells = node.widthCells != null ? Math.min(node.widthCells, w) : w;
        const block: Block = [];
        const markerSeg: Segment = {
          text: " ".repeat(widthCells),
          media: {
            tag: node.tag,
            src: node.src,
            alt: node.alt,
            widthCells,
            heightLines,
            attrs: node.attrs,
          },
        };
        block.push(padLine([markerSeg], w));
        for (let i = 1; i < heightLines; i++) {
          block.push(padLine([" ".repeat(w)], w));
        }
        return block;
      };
    }
    default:
      return blank();
  }
}
